import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { IonicModule } from '@ionic/angular';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { CorrelationIdInterceptor } from './core/interceptor/correlation-id.interceptor';
import { MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalBroadcastService, MsalInterceptor, MsalService } from '@azure/msal-angular';
import { AuthServiceBase } from './core/auth/auth-service-base';
import { MSALInstanceFactory, MSALInterceptorConfigFactory } from 'src/msal-config';
import { AccountInfo, InteractionRequiredAuthError, IPublicClientApplication } from '@azure/msal-browser';
import { MsalAuthService } from './core/auth/msal/msal-auth.service';
import { AuthRecoveryInterceptor } from './core/interceptor/auth-recovery.interceptor';

const MSAL_CACHE_SIGNATURE_KEY = 'purchasing-reports.msal-cache-signature';

function getMsalCacheSignature(): string {
    return JSON.stringify({
        clientId: '20314930-76aa-46a6-acd6-a348eb04bf47',
        authority: 'https://login.microsoftonline.com/3bc062e4-ac9d-4c17-b4dd-3aad637ff1ac',
        apiScope: environment.AZURE_API_SCOPE,
        scopes: environment.AZURE_SCOPES,
        protectedResources: [
            environment.url.api_generic_tools,
            environment.url.api_abaco
        ]
    });
}

async function sanitizeMsalCache(instance: IPublicClientApplication): Promise<void> {
    const currentSignature = getMsalCacheSignature();
    const cachedSignature = localStorage.getItem(MSAL_CACHE_SIGNATURE_KEY);

    if (cachedSignature === currentSignature) {
        return;
    }

    await instance.clearCache();
    localStorage.setItem(MSAL_CACHE_SIGNATURE_KEY, currentSignature);
}

function getStartupScopes(): string[] {
    return environment.AZURE_SCOPES.genericTools?.length
        ? environment.AZURE_SCOPES.genericTools
        : [environment.AZURE_API_SCOPE];
}

async function ensureFreshApiToken(
    instance: IPublicClientApplication,
    account: AccountInfo
): Promise<void> {
    const scopes = getStartupScopes();

    try {
        await instance.acquireTokenSilent({
            account,
            scopes,
            forceRefresh: true
        });
    } catch (error) {
        if (!(error instanceof InteractionRequiredAuthError)) {
            throw error;
        }

        await instance.clearCache({ account });
        localStorage.setItem(MSAL_CACHE_SIGNATURE_KEY, getMsalCacheSignature());
        await instance.loginRedirect({
            scopes,
            redirectStartPage: window.location.href
        });
    }
}

export function initializeMsal(
    instance: IPublicClientApplication,
    broadcastService: MsalBroadcastService
): () => Promise<void> {
    return async () => {
        // Instantiating the broadcast service before redirect handling prevents
        // inProgress$ from remaining forever in InteractionStatus.Startup.
        void broadcastService;
        await instance.initialize();

        const result = await instance.handleRedirectPromise();
        if (result?.account) {
            localStorage.setItem(MSAL_CACHE_SIGNATURE_KEY, getMsalCacheSignature());
            instance.setActiveAccount(result.account);
        } else {
            await sanitizeMsalCache(instance);
        }

        const account = result?.account ?? instance.getActiveAccount() ?? instance.getAllAccounts()[0];

        if (!account) {
            await instance.loginRedirect({
                scopes: getStartupScopes(),
                redirectStartPage: window.location.href
            });
            return;
        }

        instance.setActiveAccount(account);
        await ensureFreshApiToken(instance, account);
    };
}

@NgModule({ declarations: [AppComponent],
    bootstrap: [AppComponent],
    schemas: [], imports: [BrowserModule,
        IonicModule.forRoot(),
        FormsModule,
        ReactiveFormsModule,
        AppRoutingModule,
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: environment.production,
            registrationStrategy: 'registerWhenStable:30000'
        })], providers: [
        {
            provide: AuthServiceBase,
            useClass: MsalAuthService
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CorrelationIdInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthRecoveryInterceptor,
            multi: true
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeMsal,
            deps: [MSAL_INSTANCE, MsalBroadcastService],
            multi: true
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory
        },
        MsalService,
        MsalBroadcastService,
        provideHttpClient(withInterceptorsFromDi()),
        DatePipe
    ] })
export class AppModule { }
